import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { AwardIcon, ShieldCheckIcon, HeartIcon, GraduationCapIcon } from 'lucide-react';
const DR_NNABUE_IMG = 'https://cdn.magicpatterns.com/uploads/sgjvpBBFqWH9mG1QuuAzAG/Alex-Nnabue-1-removebg-preview.png';
const DR_ADAEZE_IMG = 'https://cdn.magicpatterns.com/uploads/n5g2k3qyzhafVRmAakrjJ6/Dr-Adaeze-Nnabue134-230x230.jpg';
const MS_BERNICE_IMG = 'https://cdn.magicpatterns.com/uploads/dyV6wsmgWRjyPdVHkZQrT5/Ms_Bernice.jpg';
const teamMembers = [{
  name: 'Dr. Nnabue',
  role: 'Lead Optometrist',
  experience: '20+ Years Experience',
  bio: 'Founder of Visual Eyes Group, Dr. Nnabue is dedicated to providing first-class eye care to the community. His expertise spans comprehensive eye exams, glaucoma management, and advanced diagnostic care.',
  image: DR_NNABUE_IMG,
  credentials: [{
    icon: AwardIcon,
    text: 'Board Certified Optometrist'
  }, {
    icon: ShieldCheckIcon,
    text: 'Glaucoma Specialist'
  }]
}, {
  name: 'Dr. Adaeze Nnabue',
  role: 'Optometrist',
  experience: '10+ Years Experience',
  bio: 'Dr. Adaeze Nnabue specializes in comprehensive eye care, pediatric vision, and contact lens fittings. She is passionate about preventative eye health and patient education.',
  image: DR_ADAEZE_IMG,
  credentials: [{
    icon: AwardIcon,
    text: 'Board Certified Optometrist'
  }, {
    icon: HeartIcon,
    text: 'Pediatric Vision Specialist'
  }]
}, {
  name: 'Ms. Bernice',
  role: 'Office Manager',
  experience: '15+ Years in Eye Care',
  bio: 'Ms. Bernice ensures every patient has a seamless and welcoming experience from the moment they walk in. She manages scheduling, insurance coordination, and patient relations with warmth and efficiency.',
  image: MS_BERNICE_IMG,
  credentials: [{
    icon: GraduationCapIcon,
    text: 'Certified Ophthalmic Assistant'
  }, {
    icon: HeartIcon,
    text: 'Patient Care Advocate'
  }]
}];
export function AboutSection() {
  return <section id="about" className="py-20 md:py-28 bg-surface-muted" data-id="element-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-33">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-34">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-section-mobile md:text-section text-brand-dark mb-4" data-id="element-35">
            Meet Our Team
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-subtitle text-neutral-text/80" data-id="element-36">
            Expert care from experienced professionals who put your vision
            first.
          </motion.p>
        </div>

        {/* Top row: Two doctors side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10" data-id="element-37">
          {teamMembers.slice(0, 2).map((member, index) => <motion.div key={member.name} initial={{
          opacity: 0,
          x: index === 0 ? -30 : 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-card shadow-card overflow-hidden flex flex-col sm:flex-row" data-id="element-38">
              <div className="sm:w-2/5 bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center overflow-hidden" data-id="element-39">
                <img src={member.image} alt={`${member.name} - ${member.role}`} className="w-full h-auto max-h-[320px] object-contain object-center" loading="lazy" data-id="element-40" />
              </div>
              <div className="p-8 sm:w-3/5 flex flex-col justify-center" data-id="element-41">
                <div className="inline-block px-3 py-1 bg-brand-gold-pale text-brand-dark text-xs font-bold rounded-full mb-3 w-fit" data-id="element-42">
                  {member.experience}
                </div>
                <h3 className="text-2xl font-heading font-bold text-brand-dark mb-1" data-id="element-43">
                  {member.name}
                </h3>
                <p className="text-brand-gold font-medium mb-4" data-id="element-44">
                  {member.role}
                </p>
                <p className="text-neutral-text/70 mb-6 text-sm leading-relaxed" data-id="element-45">
                  {member.bio}
                </p>
                <ul className="space-y-2 text-sm text-neutral-text/80 mb-6" data-id="element-46">
                  {member.credentials.map(cred => {
                const Icon = cred.icon;
                return <li key={cred.text} className="flex items-center gap-2" data-id="element-47">
                        <Icon className="w-4 h-4 text-brand-gold" data-id="element-48" /> {cred.text}
                      </li>;
              })}
                </ul>
                <a href="https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0" target="_blank" rel="noreferrer" className="inline-block w-full sm:w-auto text-center bg-brand-dark text-white px-6 py-2.5 rounded-button font-heading font-semibold text-sm hover:bg-brand-dark/90 transition-colors focus-ring" data-id="element-49">
                  Book Appointment
                </a>
              </div>
            </motion.div>)}
        </div>

        {/* Bottom row: Office Manager centered */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto" data-id="element-50">
          <div className="bg-white rounded-card shadow-card overflow-hidden flex flex-col sm:flex-row" data-id="element-51">
            <div className="sm:w-2/5 bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center overflow-hidden" data-id="element-52">
              <img src={teamMembers[2].image} alt={`${teamMembers[2].name} - ${teamMembers[2].role}`} className="w-full h-auto max-h-[320px] object-contain object-center" loading="lazy" data-id="element-53" />
            </div>
            <div className="p-8 sm:w-3/5 flex flex-col justify-center" data-id="element-54">
              <div className="inline-block px-3 py-1 bg-brand-gold-pale text-brand-dark text-xs font-bold rounded-full mb-3 w-fit" data-id="element-55">
                {teamMembers[2].experience}
              </div>
              <h3 className="text-2xl font-heading font-bold text-brand-dark mb-1" data-id="element-56">
                {teamMembers[2].name}
              </h3>
              <p className="text-brand-gold font-medium mb-4" data-id="element-57">
                {teamMembers[2].role}
              </p>
              <p className="text-neutral-text/70 mb-6 text-sm leading-relaxed" data-id="element-58">
                {teamMembers[2].bio}
              </p>
              <ul className="space-y-2 text-sm text-neutral-text/80" data-id="element-59">
                {teamMembers[2].credentials.map(cred => {
                const Icon = cred.icon;
                return <li key={cred.text} className="flex items-center gap-2" data-id="element-60">
                      <Icon className="w-4 h-4 text-brand-gold" data-id="element-61" /> {cred.text}
                    </li>;
              })}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}